/**
 * The only way third-party code gets onto a marketing page.
 *
 * This module is the only code in the marketing site that creates a `<script>`
 * element (a source scan in `test/old-origin-wipe.test.ts` holds it to that),
 * and no marketing HTML carries a third-party `<script>` tag
 * (`test/signup-isolation.test.ts` checks the built pages). So every
 * third-party script that ever runs on `https://lifekees.com` goes through
 * {@link loadThirdPartyScript}, and that function will not append one until:
 *
 *  1. this page load's wipe of the old app's storage has **finished and is
 *     complete** (`old-origin/wipe.ts`). Incomplete — typically an old-app tab
 *     holding the database open — means it throws, and the provider never
 *     loads on this page load. Fail closed: signup shows a notice instead of
 *     the widget;
 *  2. the provider is in the registry (`site/third-party.config.ts`), and the
 *     URL appended is the registry's, not a caller's;
 *  3. this page is one the provider is registered for. The page's `<meta>` CSP
 *     is generated from the same registry, so the browser would refuse it
 *     anyway; this says so with an error instead of a console line.
 *
 * The ordering is structural, not a matter of who imports what first: the
 * script element cannot exist before `await oldOriginWiped` has returned a
 * complete report, because it is created after that line.
 */
import { THIRD_PARTIES, type SitePage, type ThirdParty, type ThirdPartyName } from '../third-party.config.js';
import { oldOriginWiped, type WipeReport } from './old-origin/wipe.js';

/** The old app's storage could not be fully deleted, so no third party loads. */
export class OldOriginNotWipedError extends Error {
  override name = 'OldOriginNotWipedError';
  constructor(readonly report: WipeReport) {
    super(`the old app's storage on this origin is not fully deleted: ${JSON.stringify(report.steps)}`);
  }
}

/** A provider, URL or page the registry does not allow. */
export class UnregisteredThirdPartyError extends Error {
  override name = 'UnregisteredThirdPartyError';
}

export interface LoadDeps {
  /** The wipe to wait for. Defaults to this page load's. */
  wiped?: Promise<WipeReport>;
  /** The registry. Defaults to `site/third-party.config.ts`; tests pass their own. */
  registry?: Readonly<Record<string, ThirdParty>>;
  document?: Document;
}

/** Which marketing page this is: `<html data-page="…">`, set in each page's HTML. */
function currentPage(doc: Document): string | undefined {
  return doc.documentElement.dataset['page'];
}

function allowedScript(provider: ThirdParty): string {
  let url: URL;
  try {
    url = new URL(provider.script);
  } catch {
    throw new UnregisteredThirdPartyError(`not a URL: ${provider.script}`);
  }
  if (url.protocol !== 'https:' || !provider.scriptSrc.includes(url.origin)) {
    throw new UnregisteredThirdPartyError(`${provider.script} is not served from the provider's own script-src`);
  }
  return url.href;
}

/**
 * Load a registered provider's script on this page, after the old-origin wipe.
 * Resolves when the script has loaded; rejects (and appends nothing) when the
 * wipe is incomplete or the provider/page is not registered.
 */
export async function loadThirdPartyScript(name: ThirdPartyName | string, deps: LoadDeps = {}): Promise<void> {
  const report = await (deps.wiped ?? oldOriginWiped);
  if (!report.complete) throw new OldOriginNotWipedError(report);

  const registry: Readonly<Record<string, ThirdParty>> = deps.registry ?? THIRD_PARTIES;
  const provider = Object.prototype.hasOwnProperty.call(registry, name) ? registry[name] : undefined;
  if (!provider) throw new UnregisteredThirdPartyError(`no third party called ${JSON.stringify(name)} is registered`);

  const doc = deps.document ?? document;
  const page = currentPage(doc);
  if (!page || !provider.pages.includes(page as SitePage)) {
    throw new UnregisteredThirdPartyError(`${name} is not registered for page ${JSON.stringify(page)}`);
  }

  const src = allowedScript(provider);
  await new Promise<void>((resolve, reject) => {
    const script = doc.createElement('script');
    script.src = src;
    script.async = true;
    // No referrer beyond the origin (nginx's Referrer-Policy says the same).
    // Not `crossOrigin`: that makes the load depend on the provider sending
    // CORS headers for its script, which none promises to.
    script.referrerPolicy = 'strict-origin';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`${name} failed to load from ${src}`));
    doc.head.append(script);
  });
}
